import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle2, 
  XCircle,
  AlertCircle,
  MapPin,
  FileText,
  BarChart3,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useState } from 'react';
import type { AuditException } from '@/types/asset-audit';

interface ExceptionMetricsCardsProps {
  exceptions: AuditException[];
  onFilterByType?: (type: string) => void;
  onFilterBySeverity?: (severity: string) => void;
  onFilterByStatus?: (status: string) => void;
  onFilterFollowUp?: (category: 'overdue' | 'dueSoon' | 'upcoming') => void;
  onResetFilters?: () => void;
}

export function ExceptionMetricsCards({
  exceptions,
  onFilterByType,
  onFilterBySeverity,
  onFilterByStatus,
  onFilterFollowUp,
  onResetFilters,
}: Readonly<ExceptionMetricsCardsProps>) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Calculate metrics
  const byType: Record<string, number> = exceptions.reduce((acc, ex) => {
    acc[ex.exceptionType] = (acc[ex.exceptionType] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const bySeverity: Record<string, number> = exceptions.reduce((acc, ex) => {
    acc[ex.severity] = (acc[ex.severity] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const byStatus: Record<string, number> = exceptions.reduce((acc, ex) => {
    acc[ex.status] = (acc[ex.status] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const now = new Date();
  const followUp = {
    overdue: exceptions.filter(ex => ex.followUpDueDate && new Date(ex.followUpDueDate) < now).length,
    dueSoon: exceptions.filter(ex => {
      if (!ex.followUpDueDate) return false;
      const followUpDate = new Date(ex.followUpDueDate);
      const daysUntil = Math.ceil((followUpDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntil >= 0 && daysUntil <= 7;
    }).length,
    upcoming: exceptions.filter(ex => {
      if (!ex.followUpDueDate) return false;
      const followUpDate = new Date(ex.followUpDueDate);
      const daysUntil = Math.ceil((followUpDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntil > 7;
    }).length,
  };

  const typeIcons: Record<string, any> = {
    LocationMismatch: MapPin,
    ConditionIssue: AlertTriangle,
    MissingDocumentation: FileText,
    ValueDiscrepancy: AlertCircle,
  };

  const severityColors: Record<string, { dot: string }> = {
    Critical: { dot: 'bg-red-500' },
    High: { dot: 'bg-orange-500' },
    Medium: { dot: 'bg-yellow-500' },
    Low: { dot: 'bg-blue-500' },
  };

  return (
    <Card className="relative overflow-hidden mb-6">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />
      
      <CardHeader className="relative pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-blue-500/10">
              <BarChart3 className="size-5 text-blue-500" />
            </div>
            <CardTitle className="text-base font-medium">Exception Overview</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetFilters}
              className="h-8 px-3 text-xs"
            >
              Reset
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="h-8 w-8 p-0"
            >
              {isCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 divide-x divide-border">
          {/* By Type */}
          <div className="space-y-3 px-4 first:pl-0">
            <h4 className="text-sm font-semibold text-muted-foreground">By Type</h4>
            {Object.entries(byType).map(([type, count]) => {
              const Icon = typeIcons[type] || AlertCircle;
              const percentage = (count / exceptions.length) * 100;
              return (
                <div key={type}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Icon className="h-3 w-3 text-muted-foreground" />
                        <button 
                          type="button"
                          className="text-sm font-medium cursor-pointer hover:underline bg-transparent border-none p-0"
                          onClick={() => onFilterByType?.(type)}
                        >
                          {type.replace(/([A-Z])/g, ' $1').trim()}
                        </button>
                    </div>
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
          </div>

          {/* By Status */}
          <div className="space-y-3 px-4">
            <h4 className="text-sm font-semibold text-muted-foreground">By Status</h4>
            {Object.entries(byStatus).map(([status, count]) => {
              const percentage = (count / exceptions.length) * 100;
              const getStatusIcon = () => {
                if (status === 'Resolved') return CheckCircle2;
                if (status === 'InProgress') return Clock;
                return XCircle;
              };
              const StatusIcon = getStatusIcon();
              return (
                <div key={status}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <StatusIcon className="h-3 w-3 text-muted-foreground" />
                        <button 
                          type="button"
                          className="text-sm font-medium cursor-pointer hover:underline bg-transparent border-none p-0"
                          onClick={() => onFilterByStatus?.(status)}
                        >
                          {status.replace(/([A-Z])/g, ' $1').trim()}
                        </button>
                    </div>
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
          </div>

          {/* By Severity */}
          <div className="space-y-2 px-4">
            <h4 className="text-sm font-semibold text-muted-foreground mb-3">By Severity</h4>
            {Object.entries(bySeverity).map(([severity, count]) => {
              const colors = severityColors[severity];
              return (
                <button 
                  type="button"
                  key={severity}
                  className="flex items-center justify-between cursor-pointer hover:bg-accent/50 rounded-md p-2 transition-colors w-full bg-transparent border-none text-left"
                  onClick={() => onFilterBySeverity?.(severity)}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                    <span className="text-sm font-medium">{severity}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">{count}</Badge>
                </button>
              );
            })}
          </div>

          {/* Requires Follow-up */}
          <div className="space-y-2 px-4">
            <h4 className="text-sm font-semibold text-muted-foreground mb-3">Requires Follow-up</h4>
            <button 
              type="button"
              className="flex items-center justify-between cursor-pointer hover:bg-accent/50 rounded-md p-2 transition-colors w-full bg-transparent border-none text-left"
              onClick={() => onFilterFollowUp?.('overdue')}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-sm font-medium">Overdue</span>
              </div>
              <Badge variant="secondary" className="text-xs">{followUp.overdue}</Badge>
            </button>
            <button 
              type="button"
              className="flex items-center justify-between cursor-pointer hover:bg-accent/50 rounded-md p-2 transition-colors w-full bg-transparent border-none text-left"
              onClick={() => onFilterFollowUp?.('dueSoon')}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <span className="text-sm font-medium">Due Soon (7 days)</span>
              </div>
              <Badge variant="secondary" className="text-xs">{followUp.dueSoon}</Badge>
            </button>
            <button 
              type="button"
              className="flex items-center justify-between cursor-pointer hover:bg-accent/50 rounded-md p-2 transition-colors w-full bg-transparent border-none text-left"
              onClick={() => onFilterFollowUp?.('upcoming')}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-sm font-medium">Upcoming</span>
              </div>
              <Badge variant="secondary" className="text-xs">{followUp.upcoming}</Badge>
            </button>
          </div>
        </div>
        </CardContent>
      )}
    </Card>
  );
}
